// Shared helpers for normalizers that must leave code untouched.
//
// Fenced blocks: a fence opens with a run of three or more backticks or
// tildes and closes only on a run of the *same* character at least as long
// as the opener (CommonMark 4.5). Toggling on any fence-looking line would
// let a `~~~` inside a backtick block, or a ``` inside a four-backtick block,
// end the block early and expose its contents to rewriting.
//
// Inline code: `mapOutsideInlineCode` applies a transform only to the parts
// of a line that are not inside backtick spans.

export type FenceMarker = { char: '`' | '~'; length: number };

const OPENING_FENCE = /^ {0,3}(`{3,}|~{3,})/;

export function openingFence(line: string): FenceMarker | null {
  const match = OPENING_FENCE.exec(line);
  if (!match) return null;
  return { char: match[1][0] as '`' | '~', length: match[1].length };
}

export function closesFence(line: string, fence: FenceMarker): boolean {
  const match = /^ {0,3}(`{3,}|~{3,})\s*$/.exec(line);
  return match !== null && match[1][0] === fence.char && match[1].length >= fence.length;
}

export function mapOutsideInlineCode(line: string, transform: (text: string) => string): string {
  if (!line.includes('`')) return transform(line);
  let out = '';
  let index = 0;
  while (index < line.length) {
    const open = line.indexOf('`', index);
    if (open < 0) {
      out += transform(line.slice(index));
      break;
    }
    const close = line.indexOf('`', open + 1);
    out += transform(line.slice(index, open));
    if (close < 0) {
      // Unterminated span (possibly still streaming): keep the rest verbatim.
      out += line.slice(open);
      break;
    }
    out += line.slice(open, close + 1);
    index = close + 1;
  }
  return out;
}
